import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { Avatar } from '../../ui/Display/Display';
import { Icon } from '../../ui/Icon/Icon';
import { IconButton } from '../../ui/IconButton/IconButton';
import { MenuDropdown } from '../../ui/Menu/Menu';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';

function SearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <form
      role="search"
      className="relative hidden w-full max-w-xs md:block"
      onSubmit={(e) => {
        e.preventDefault();
        if (!q.trim()) return;
        navigate(`/customers?q=${encodeURIComponent(q.trim())}`);
        setQ('');
      }}
    >
      <Icon name="search" size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search customers by name or phone…"
        aria-label="Search customers"
        className="h-10 w-full rounded-lg border border-line bg-navy-50/60 pl-9 pr-3 text-sm text-navy-900 outline-none transition-colors placeholder:text-ink-400 hover:border-navy-300 focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/15"
      />
    </form>
  );
}

export interface HeaderProps {
  /** Opens the sidebar drawer (phones and tablets). */
  onMenu: () => void;
  /** Collapses or expands the desktop sidebar. */
  onToggleSidebar: () => void;
  collapsed: boolean;
}

/** White topbar: sidebar toggle, breadcrumbs, customer search and the account menu. */
export function Header({ onMenu, onToggleSidebar, collapsed }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <IconButton icon="menu" label="Open menu" className="-ml-2 lg:hidden" onClick={onMenu} />
        <IconButton
          icon="panelLeft"
          label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          size="sm"
          className="-ml-2 hidden text-ink-400 lg:inline-flex"
          onClick={onToggleSidebar}
        />
        <span className="hidden h-5 w-px bg-line lg:block" aria-hidden="true" />
        <Breadcrumbs />
        <div className="flex-1" />
        <SearchBox />
        <MenuDropdown
          align="end"
          width={240}
          trigger={
            <button type="button" aria-label="Account" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-steel focus-visible:ring-offset-2">
              <Avatar name={user?.name} size={34} />
            </button>
          }
          header={
            <div className="border-b border-line px-3 pb-2.5 pt-2">
              <p className="truncate font-medium">{user?.name}</p>
              <p className="text-[13px] capitalize text-ink-400">{user?.role}</p>
              <p className="text-[13px] text-ink-400">{user?.phone}</p>
            </div>
          }
          items={[
            { label: 'Change password', icon: 'settings', onClick: () => navigate('/account/password') },
            { divider: true },
            { label: 'Log out', icon: 'logout', danger: true, onClick: logout },
          ]}
        />
      </div>
    </header>
  );
}

export default Header;
